import type { ActionCreator, ActionMessage } from 'satcheljs';
import { createStore } from 'owa-satcheljs';
import { action, mutator, orchestrator } from 'satcheljs';
import { logUsage } from 'owa-analytics';
import type { TraceErrorObject, CustomDataMap } from 'owa-analytics-types';
import type { MailboxInfo } from 'owa-client-types';

/**
 * This function creates an observable async operation.
 *
 * @param name A name for the async operation, used to generate store and data point names.
 * @param createActionMessage A callback that defines the parameters signature of the async operation and produces an action message, which is passed to the invoke callback and other events in the operation.
 * @param getKey Returns the key of an operation, given the action messaged produced by createActionMessage. For any given key, only one operation will be started at a time.
 * @param invoke A callback that implements the async operation. It receives the action message produced by createActionMessage as parameter.
 * @param getMailboxInfo Callback that returns the MailboxInfo associated with the operation, used to attribute the Accelerator_START / _SUCCEEDED / _FAILED telemetry to a specific account. See https://aka.ms/owa-analytics-mailboxinfo.
 * @param createCustomData A callback that defines custom data that will be logged in _SUCCEEDED and _FAILED events.
 * @returns A set of functions that allows the application to start an operation, query is status and subscribe to events.
 *
 * The expected usage of this function is to bind UX elements to
 * the state of an asynchronous operation.
 *
 * For example, some UX might show a list of items and a refresh button,
 * along with a spinner that indicates the application is loading items.
 * The application will call createObservableOperation, with the callback
 * that loads the items to show on the list. In return, it gets back the
 * start function, which it can associate with the onClick of the refresh
 * button, and the isInProgress function, which it can use to determine if
 * the refresh button should be disabled and if the spinner should show up.
 *
 * When start is called, a check is made to see if an existing operation with
 * the same key is already in progress, and if so, the call is ignored, otherwise
 * the state of the operation is tracked and the invoke callback is called. With
 * this in mind, the application is responsible for deciding if each call to
 * createActionMessage should produce a unique key or if there is an underlying
 * key that will be used to prevent multiple calls to start.
 *
 * The return value includes a set of satcheljs actions that allow the application
 * to bind additional mutators or orchestrators to the state changes.
 * These actions must NOT be explicitly invoked by the application.
 */
export default function createObservableOperation<
    K extends string,
    C extends (...args: any) => {},
    A extends ReturnType<C>,
    R
>(
    name: string,
    createActionMessage: C,
    getKey: (actionMessage: A) => K,
    invoke: (actionMessage: A) => Promise<R>,
    getMailboxInfo: (actionMessage: A) => MailboxInfo,
    createCustomData?: (actionMessage: A) => CustomDataMap
): {
    /** Checks if an operation with the given key is in progress. */
    isInProgress: (key: K) => boolean;

    /** Returns the error of the last invocation of the operation with the given key. */
    getLastError: (key: K) => TraceErrorObject | undefined;

    /** Start a new operation, provided another operation with the same key is not already in progress. */
    start: C;

    /** Start a new operation, provided another operation with the same key is not already in progress, and returns a promise that resolves when the operation completes. */
    startAsync: (...args: Parameters<C>) => Promise<R>;

    /** Event raised when an operation starts executing. Attach to mutators or orchestrators. DO NOT INVOKE DIRECTLY. */
    onStarted: (actionMessage: A, promise: Promise<R>) => A;

    /** Event raised when an operation finishes executing. Attach to mutators or orchestrators. DO NOT INVOKE DIRECTLY. */
    onEnded: (actionMessage: A) => A;

    /** Event raised when an operation completes successfully. Attach to mutators or orchestrators. DO NOT INVOKE DIRECTLY. */
    onSucceeded: (
        actionMessage: A & {
            result: R;
        }
    ) => A & {
        result: R;
    };

    /** Event raised when an operation fails. Attach to mutators or orchestrators. DO NOT INVOKE DIRECTLY. */
    onFailed: (
        actionMessage: A,
        error?: TraceErrorObject
    ) => A & {
        error?: TraceErrorObject;
    };
} {
    // Store
    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE!, this function can only be called in the module scope.
     *	> Function should only be invoked in module scope */
    const { inProgress, errors } = createStore(name, {
        inProgress: new Map<K, Promise<R>>(),
        errors: new Map<K, TraceErrorObject | undefined>(),
    })();

    // Selector
    const isInProgress = (key: K) => !!inProgress.get(key);
    const getLastError = (key: K) => errors.get(key);

    const start = action(name, createActionMessage as unknown as ActionCreator<A>);

    // Events
    const similarActionMessage = (actionMessage: A) => actionMessage;
    const onStarted = action(name + '_STARTED', (actionMessage: A, promise: Promise<R>) => ({
        ...actionMessage,
        promise,
    }));
    const onEnded = action(name + '_ENDED', similarActionMessage);

    const onSucceeded = action(
        name + '_SUCCEEDED',
        (
            actionMessage: A & {
                result: R;
            }
        ) => actionMessage
    );

    const onFailed = action(name + '_FAILED', (actionMessage: A, error?: TraceErrorObject) => ({
        ...actionMessage,
        error,
    }));

    // Mutators
    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * * Baseline. DO NOT COPY AND PASTE!
     *	> Function should only be invoked in module scope */
    mutator(onStarted, actionMessage => {
        const key = getKey(actionMessage);
        inProgress.set(key, actionMessage.promise);
        errors.delete(key);
    });

    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * * Baseline. DO NOT COPY AND PASTE!
     *	> Function should only be invoked in module scope */
    mutator(onFailed, actionMessage => {
        const key = getKey(actionMessage);
        errors.set(key, actionMessage.error);
    });

    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * * Baseline. DO NOT COPY AND PASTE!
     *	> Function should only be invoked in module scope */
    mutator(onEnded, actionMessage => {
        const key = getKey(actionMessage);
        inProgress.delete(key);
    });

    function startAsync(...args: Parameters<C>) {
        const actionMessage = start(...(args as any));

        logUsage('Accelerator_START', getMailboxInfo(actionMessage), {
            ...createCustomData?.(actionMessage),
            dynamicEventName: name,
        });

        const key = getKey(actionMessage);
        return inProgress.get(key) || Promise.reject(getLastError(key));
    }

    // Orchestrators
    /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
     * Move this function to the module scope or wrapped it on a once function
     *	> Function should only be invoked in module scope */
    orchestrator(start, async actionMessage => {
        const key = getKey(actionMessage);
        /* oxlint-disable-next-line eslint/no-unused-vars -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by removing the unused variable.
         *	> Variable type, dp is being unused, if this is not needed, please remove. */
        const { type, dp, ...args } = actionMessage as ActionMessage;
        const actionMessageWithoutType = args as A;
        if (!isInProgress(key)) {
            try {
                const promise = invoke(actionMessage);
                onStarted(actionMessageWithoutType, promise);
                const result = await promise;

                onSucceeded({ ...actionMessageWithoutType, result });

                logUsage(
                    'Accelerator_SUCCEEDED',
                    getMailboxInfo({ ...actionMessageWithoutType, result } as A),
                    {
                        ...createCustomData?.({ ...actionMessageWithoutType, result } as A),
                        dynamicEventName: name + '_SUCCEEDED',
                    }
                );
            } catch (e) {
                onFailed({ ...actionMessageWithoutType }, e as TraceErrorObject);
                /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks -- (https://aka.ms/OWALintWiki)
                 * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error, use PerformanceDatapoint instead.
                 *	> Do not call 'logUsage' from 'owa-analytics' inside catch or finally blocks */
                logUsage('Accelerator_FAILED', getMailboxInfo(actionMessageWithoutType), {
                    ...createCustomData?.(actionMessageWithoutType),
                    dynamicEventName: name + '_FAILED',
                });
            } finally {
                onEnded(args as A);
            }
        }
    });

    return {
        isInProgress,
        getLastError,
        start: start as unknown as C,
        startAsync,
        onStarted,
        onEnded,
        onSucceeded,
        onFailed,
    };
}
